<template>
    <div class="ScenarioHistory">
        <ScenarioTestcaseCreator ref="ScenarioTestcaseCreator"></ScenarioTestcaseCreator>
        <SampleEditor ref="SampleEditor"></SampleEditor>
        <div class="page-header">
            <div class="btn-custom-normal mr-12" style="width: 32px" @click="backToScenario" :title="$f.tc('back')">
                <img src="/img/scenario/back.svg" alt="icon-back" style="transform: scale(1.2)">
            </div>
            <div class="page-title">
                {{ $f.tc('botHistory.label') }} -
                <span v-if="scenario" @click="backToScenario" class="name">{{ scenario.name }}</span>
            </div>
            <div class="btn-icon ml-6" @click="resetSearch">
                <v-icon color="#727885" size="24">mdi-refresh</v-icon>
            </div>
        </div>
        <v-layout class="ScenarioHistoryBody">
            <!--List chats-->
            <div class="ScenarioHistoryBodyLeft">
                <div class="px-12" style="padding-top: 12px">
                    <TimeRangePicker
                        ref="TimeRangePicker"
                        v-model="timeRange"
                    ></TimeRangePicker>
                </div>
                <div class="centerY mt-8 px-12" style="gap: 8px">
                    <div
                        v-for="item in historyTypes"
                        :key="item.value"
                        class="filter-chip"
                        :class="item.value === filters.type ? 'chip-activated' : ''"
                        @click="selectType(item)"
                    >
                        {{ item.text }}
                    </div>
                </div>
                <div style="position: relative; border-bottom: 1px solid #E4EDFA; padding-bottom: 12px"
                     class="mt-8 px-12">
                    <v-text-field
                        hide-details
                        v-model="searchKey"
                        :placeholder="$f.tc('searchBy.phoneNameCallId')"
                        @focusout="resetSearch"
                        @keypress.enter="resetSearch"
                        class="input-custom"
                    ></v-text-field>
                    <img
                        src="/img/scenario/search.svg"
                        style="position: absolute; right: 20px; top: 5px; transform: scale(0.8); cursor: pointer"
                        @click="resetSearch"
                        alt="icon-search">
                </div>
                <div class="log-container fci-scrollbar" ref="LogContainer" v-on="scrollHandlers">
                    <div v-if="callList.length === 0 && !loading" class="centerXY no-data">
                        <img src="/img/debug/not-found.svg" alt="icon-not-found">
                        <div class="no-data-title">
                            {{ $f.tc('noDataAvailable') }}
                        </div>
                        <div class="no-data-des">
                            {{ $f.tc('notFoundCall') }}
                        </div>
                    </div>
                    <div
                        v-else
                        v-for="(call, i) in callList"
                        :key="i"
                        class="log-item"
                        @click="updateActiveCall(call)"
                        :class="{active: call.id === activeCall.id}"
                    >
                        <v-layout class="log-item-phone">
                            <div class="log-item-name">{{ call.name }}</div>
                            <v-spacer></v-spacer>
                            {{ call['created_time'] | formatDateTime }}
                        </v-layout>
                        <div class="log-item-phone">
                            {{ call.phone }}
                        </div>
                        {{ call.id }}
                    </div>
                    <v-layout justify-center v-if="loading" class="px-20 py-12">
                        <v-icon class="tm-spin">mdi-loading</v-icon>
                    </v-layout>
                </div>
            </div>
            <div class="detail-tabs">
                <!--Title Chat-->
                <div class="px-12 py-12" style="border-bottom: 1px solid #e4edfa;" v-if="activeCall.id">
                    <div class="display-flex align-center justify-space-between">
                        <!-- Add tabs on the left -->
                        <v-tabs v-model="currentTab" class="history-tabs">
                            <v-tab>{{ $f.tc('overview') }}</v-tab>
                            <v-tab>{{ $f.tc('transcript') }}</v-tab>
                        </v-tabs>

                        <!-- Move List actions for history to the right -->
                        <div class="display-flex align-center">
                            <!--Button copy call ID-->
                            <v-tooltip top>
                                <template v-slot:activator="{on}">
                                    <div class="btn-tag" @click="copyText(activeCall.id)" v-on="on">
                                        <img src="/img/scenario/copy_blue.svg" alt="icon-copy"
                                             style="width: 16px; margin-right: 3px">
                                        {{ $f.tc('botHistory.callId') }}
                                    </div>
                                </template>
                                <span>{{ activeCall.id }}</span>
                            </v-tooltip>
                            <!--Button copy customer name-->
                            <v-tooltip top>
                                <template v-slot:activator="{on}">
                                    <div class="btn-tag" @click="copyText(activeCall.name)" v-on="on">
                                        <img src="/img/scenario/copy_blue.svg" alt="icon-copy"
                                             style="width: 16px; margin-right: 3px">
                                        {{ $f.tc('botHistory.customerName') }}
                                    </div>
                                </template>
                                <span>{{ activeCall.name }}</span>
                            </v-tooltip>
                            <!--Button copy customer phone-->
                            <v-tooltip top>
                                <template v-slot:activator="{on}">
                                    <div class="btn-tag" @click="copyText(activeCall.phone)" v-on="on">
                                        <img src="/img/scenario/copy_blue.svg" alt="icon-copy"
                                             style="width: 16px; margin-right: 3px">
                                        {{ $f.tc('botHistory.customerPhone') }}
                                    </div>
                                </template>
                                <span>{{ activeCall.phone }}</span>
                            </v-tooltip>
                            <!--Button copy created time-->
                            <v-tooltip top>
                                <template v-slot:activator="{on}">
                                    <div class="btn-tag" @click="copyText($f.formatDateTime(activeCall.created_time))"
                                         v-on="on">
                                        <img src="/img/scenario/copy_blue.svg" alt="icon-copy"
                                             style="width: 16px; margin-right: 3px">
                                        {{ $f.tc('botHistory.createdTime') }}
                                    </div>
                                </template>
                                <span>{{ $f.formatDateTime(activeCall.created_time) }}</span>
                            </v-tooltip>

                            <!-- Action buttons on the same line -->
                            <div class="btn-icon-2 ml-2" @click="newAudioPlayer">
                                <v-icon color="#727885" size="24">mdi-repeat</v-icon>
                            </div>
                            <div class="btn-icon-2 ml-2" @click="reloadCallHistory(activeCall)">
                                <v-icon color="#727885" size="24">mdi-refresh</v-icon>
                            </div>
                            <v-menu offset-y>
                                <template v-slot:activator="{ attrs, on }">
                                    <div class="btn-icon-2 btn-menu ml-2" v-bind="attrs" v-on="on">
                                        <img src="/img/scenario/menu.svg" alt="icon-menu"
                                             style="transform: scale(1.05)">
                                    </div>
                                </template>
                                <v-list>
                                    <v-list-tile
                                        v-for="action in historyActions"
                                        :key="action.id"
                                        @click="onAction(action.id)"
                                    >
                                        <v-list-tile-title v-text="action.text"></v-list-tile-title>
                                    </v-list-tile>
                                </v-list>
                            </v-menu>
                        </div>
                    </div>

                    <!-- Audio -->
                    <div v-if="activeCall && activeCall.id && activeCallAudioData">
                        <div class="call-audio" v-if="!newFeatureAudio">
                            <audio :src="activeCallAudioSrc" controls="controls" :title="activeCall.id"
                                   ref="audio"></audio>
                        </div>
                        <AudioWavePlayer
                            ref="AudioWavePlayer"
                            v-else
                            :audioSrc="activeCallAudioSrc"
                            :audioId="activeCall.id"
                            :audioData="activeCallAudioData"
                            @download="downloadAudio"
                        />
                    </div>
                </div>
                <div v-else class="centerXY" style="width: 100%; height: 100%">
                    <div style="font-family: 'open sans semi',serif; font-size: 16px">
                        <img src="/img/scenario/history_chat.svg" alt="icon-history-chat" style="width: 220px">
                        <div style="text-align: center; color: #545253; margin-top: -18px">
                            {{ $f.tc('noDataAvailable') }}
                        </div>
                    </div>
                </div>
                <!-- Tab content -->
                <v-tabs-items v-model="currentTab">
                    <v-tab-item>
                        <ScenarioHistoryOverview
                            :analysis-list="analysisList"
                            :call-summary="callSummary"
                            :has-waveform="newFeatureAudio"
                        ></ScenarioHistoryOverview>
                    </v-tab-item>
                    <v-tab-item>
                        <ScenarioHistoryTranscription
                            :active-history-bot="activeHistoryBot"
                            :loading-message="loadingMessage"
                            :has-waveform="newFeatureAudio"
                            :has-audio-data="!!activeCallAudioData"
                        ></ScenarioHistoryTranscription>
                    </v-tab-item>
                </v-tabs-items>
            </div>
        </v-layout>

    </div>
</template>

<script>
import ScenarioService from '@/services/scenario.service'
import TimeRangePicker from '@/modules/fptai/components/TimeRangePicker'
import $const from '@/utils/const'
import ScenarioHistoryTranscription from '@/views/Scenario/ScenarioHistoryTranscription.vue'
import ScenarioHistoryOverview from '@/views/Scenario/ScenarioHistoryOverview.vue'
import lodash from 'lodash'
import { mapState } from 'vuex'
import SampleEditor from '@/views/Scenario/nlp/SampleEditor.vue'
import ScenarioTestcaseCreator from '@/views/Scenario/ScenarioTestcaseCreator.vue'
import AudioWavePlayer from '@/components/AudioWavePlayer.vue'
import utils from '@/utils/utils'

const { NodeType } = ScenarioService
const ACTIONS_HISTORY = {
    COPY_CHAT_HISTORY: 'COPY_CHAT_HISTORY',
    TEST_CHAT_HISTORY: 'TEST_CHAT_HISTORY',
    ADD_TO_TESTSET: 'ADD_TO_TESTSET'
}

export default {
    name: 'ScenarioHistory',
    components: {
        ScenarioTestcaseCreator,
        SampleEditor,
        ScenarioHistoryOverview,
        ScenarioHistoryTranscription,
        TimeRangePicker,
        AudioWavePlayer
    },
    data () {
        return {
            currentTab: 1,
            scenario: null,
            callList: [],
            activeCall: {},
            callSummary: '',
            timeRange: 'today',
            searchKey: '',
            filters: {
                type: $const.HistoryTypes.ALL
            },
            scrollId: '',
            loading: false,
            loadingMessage: false,
            page: 1,
            page_size: 20,
            scrollEnd: false,
            scrollHandlers: {
                scroll: e => {
                    let element = e.target
                    let maxScrollTop = element.scrollHeight - element.clientHeight
                    if (maxScrollTop - 5 <= element.scrollTop) {
                        if (this.loading || this.scrollEnd) {
                            return
                        }
                        this.page++
                        this.search()
                    }
                }
            },
            activeCallAudioSrc: '',
            activeCallAudioData: null,
            activeHistoryBot: {
                callId: '',
                variableList: [],
                messageList: [],
            },
            analysisList: [],
            newFeatureAudio: localStorage.getItem('newFeatureAudio') === 'true',
        }
    },
    computed: {
        ...mapState({
            variableList: state => state.scenario.variableList,
            treeData: state => state.scenario.treeData,
            audioList: state => state.scenario.scenarioAudioList,
        }),
        historyTypes () {
            return [
                {
                    text: this.$f.tc('all'),
                    value: $const.HistoryTypes.ALL
                },
                {
                    text: this.$f.tc('callOnly'),
                    value: $const.HistoryTypes.CALL
                },
                {
                    text: this.$f.tc('chatOnly'),
                    value: $const.HistoryTypes.CHAT
                }
            ]
        },
        historyActions () {
            return [
                {
                    id: ACTIONS_HISTORY.COPY_CHAT_HISTORY,
                    text: this.$f.tc('copy') + ' ' + this.$t('chat')
                },
                {
                    id: ACTIONS_HISTORY.TEST_CHAT_HISTORY,
                    text: this.$f.tc('reappear') + ' ' + this.$t('chat')
                },
                {
                    id: ACTIONS_HISTORY.ADD_TO_TESTSET,
                    text: this.$f.tc('addTestcase')
                }
            ]
        },
    },
    mounted () {
        let stateScenario = this.$store.state.scenario.scenario
        let id = this.$route.params.scenarioId
        if (stateScenario && stateScenario.id === id) {
            this.scenario = stateScenario
        } else {
            ScenarioService.CRUD.read(id).then(
                result => this.scenario = result.data,
                this.$appService.handleRequestError(' can not load scenario')
            )
        }
        this.searchKey = this.$route.query.call_id || ''

        this.resetSearch()

        // get variable list
        this.$store.dispatch(this.$actionTypes.scenario.GET_VARIABLE, this.$route.params.scenarioId)

        //get tree data
        if (!this.treeData) {
            this.$store.dispatch(this.$actionTypes.scenario.GET_TREE_DATA, this.$route.params.scenarioId)
        }
    },
    methods: {
        onAction (idAction) {
            switch (idAction) {
                case ACTIONS_HISTORY.COPY_CHAT_HISTORY:
                    this.copyChatHistory()
                    break
                case ACTIONS_HISTORY.TEST_CHAT_HISTORY:
                    this.testChatHistory()
                    break
                case ACTIONS_HISTORY.ADD_TO_TESTSET:
                    this.addTestcase()
                    break
            }
        },
        addTestcase () {
            this.$refs.ScenarioTestcaseCreator.open().then(model => {
                // validate
                let scenarioId = this.$route.params.scenarioId
                if (!scenarioId) {
                    this.$appService.toastError('Can\'t get scenario id')
                    return
                }

                // get steps of chat test
                let lastNodeId = ''
                let lastNodeSlug = ''
                let messageList = this.activeHistoryBot.messageList

                // if last message is end call, remove it
                if (messageList[messageList.length - 1].type === 'user' && messageList[messageList.length - 1].content === '-----end call-----') {
                    messageList.pop()
                }

                let steps = messageList.reduce((filtered, message) => {
                    if (!message.action) {
                        filtered.push({
                            type: message.type === 'user' ? NodeType.DECISION : NodeType.PROCESS,
                            text: message.content,
                            node_id: lastNodeId,
                            user_attributes: message.type === 'user' ? message['input']['user_attributes'] : {},
                            slug: lastNodeSlug,
                            audio_link: message.type === 'user' ? '' : message['audioLink'],
                            audio_slug: message.type === 'user' ? '' : message['audioSlug'],

                        })
                        // add node id and slug to bot message
                        if (message.type === 'user' && message['user_attributes']) {
                            lastNodeId = message['user_attributes'] ? message['user_attributes']['current_node'] : ''
                            lastNodeSlug = message['user_attributes'] ? message['user_attributes']['current_node_slug'] : ''
                        }
                        // add node id and slug to user message
                        if (message.type === 'bot' && message['decisionNode'] && !message['nextNode']) {
                            lastNodeId = message['decisionNode'] ? message['decisionNode'].id : ''
                            lastNodeSlug = message['decisionNode'] ? message['decisionNode'].slug : ''
                        }
                        if (message.type === 'bot' && message['nextNode']) {
                            lastNodeId = message['nextNode']['next_node_id']
                            lastNodeSlug = message['nextNode']['next_node_slug']
                        }
                    }
                    return filtered
                }, [])
                steps.shift()

                // get data_collection check save
                let dataCollection = {}
                if (messageList.length > 0) {
                    let a = 1
                    let dataTestLastMessage = messageList[messageList.length - a]
                    while (!dataTestLastMessage['user_attributes']) {
                        a += 1
                        dataTestLastMessage = messageList[messageList.length - a]
                    }
                    let userAttributes = dataTestLastMessage ? (dataTestLastMessage['user_attributes'] ? dataTestLastMessage['user_attributes'] : null) : null
                    let variablesSaved = this.variableList.filter(i => i['should_save']).map(i => i.name)
                    if (userAttributes && variablesSaved.length > 0) {
                        variablesSaved.forEach(variable => {
                            dataCollection[variable] = userAttributes[variable] ? userAttributes[variable] : ''
                        })
                    }
                }
                this.createTestcase(scenarioId, { model, steps, dataCollection })
            })
        },
        createTestcase (scenarioId, data) {
            let { model, steps, dataCollection } = data
            this.$appService.showLoading()
            ScenarioService.AutomationTestService.create(
                scenarioId,
                {
                    group: model.group,
                    name: model.name,
                    scenario: scenarioId,
                    steps: steps,
                    customer_properties: this.activeHistoryBot.variableList || {},
                    data_collection: dataCollection
                }
            ).then(() => {
                this.$appService.toastSuccess(`${this.$f.tc('addTestcase')} ${this.$f.tc('successful')}`)
            }).finally(this.$appService.hideLoading)
        },
        copyChatHistory () {
            this.$utils.copyToClipboard(JSON.stringify(this.activeHistoryBot), this.$f.tc('copyHistorySuccess'))
        },
        testChatHistory () {
            this.$router.push({
                name: 'ScenarioContentEditor',
                params: { historyBot: JSON.stringify(this.activeHistoryBot) }
            })
        },
        downloadAudio () {
            if (this.activeCallAudioData) {
                utils.downloadFileFromBlob(this.activeCallAudioData, this.activeCall.id + '.wav')
            }
        },
        copyText (text) {
            this.$utils.copyToClipboard(text, this.$f.tc('copied'))
        },
        updateActiveCall (call) {
            this.activeCall = call
        },
        backToScenario () {
            this.$router.push(`/scenarios/${this.scenario.id}`)
        },
        search () {
            this.loading = true
            return ScenarioService.HistoryService.search(this.$route.params.scenarioId, {
                ...this.$refs.TimeRangePicker.getTimestamp(),
                search: this.searchKey,
                type: this.filters.type,
                page: this.page,
                page_size: this.page_size
            }).then(
                result => {
                    if (result.data.data.length > 0) {
                        let nextCallList = result.data.data.map(call => ({
                            ...call,
                            name: call.name || '---unknown---',
                            phone: call.phone || '---unknown---'
                        }))
                        this.callList = this.$lodash.uniqBy(this.callList.concat(nextCallList), item => item.id)
                    } else {
                        this.scrollEnd = true
                        if (this.page > 1) {
                            this.page--
                        }
                    }
                }
            ).finally(() => {
                this.loading = false
            })
        },

        resetSearch () {
            this.page = 1
            this.callList = []
            this.scrollEnd = false

            // Reset audio data
            this.resetAudio()

            return this.search().then(() => {
                if (this.callList.length > 0) {
                    this.activeCall = this.callList[0]
                } else {
                    this.activeCall = {}
                }
            })
        },

        selectType (item) {
            this.filters.type = item.value
            this.resetSearch()
        },
        reloadCallHistory (call) {
            this.loadCallMessage(call)
            this.loadCallAnalysis(call)
        },
        resetAudio () {
            this.activeCallAudioSrc = ''
            this.activeCallAudioData = null
        },
        loadCallMessage (call) {
            this.activeCall = call
            // if is call, load audio
            if (utils.isUUID(call.id)) {
                ScenarioService.getCallAudio(call.id).then(
                    result => {
                        this.activeCallAudioData = result.data
                        this.activeCallAudioSrc = window.URL.createObjectURL(new Blob([result.data], { type: 'audio/wav' }))
                    }
                ).catch(() => {
                    this.resetAudio()
                })
            } else {
                this.resetAudio()
            }

            // load messages
            this.loadingMessage = true
            ScenarioService.HistoryService.read(this.$route.params.scenarioId, call.id).then(
                result => {
                    let list = []
                    let logList = lodash.orderBy(result.data, '_source.created_time', 'desc')
                    let goToNode = ''
                    let linkList = this.treeData.linkList
                    let decisionNode = ''
                    let nextNode = null
                    for (let log of logList) {
                        //bot say
                        const detailMessage = log['_source'].input.message
                        let output = Array.isArray(log['_source'].output) ? log['_source'].output[0] : log['_source'].output
                        let audioLink = output['audio_link'] ? output['audio_link'] : ''
                        let audioSlug = output['audio_slug'] ? output['audio_slug'] : ''
                        let audioName = output['audio_name'] ? output['audio_name'] : ''

                        // case bot transfer agent
                        let nextActionType = output.next_action?.type
                        if (nextActionType === 'transfer_call') {
                            list.push({
                                type: 'bot',
                                content: `-----${nextActionType}-----`,
                                decisionNode: decisionNode,
                                audioLink: audioLink,
                                audioSlug: audioSlug,
                                audioName: audioName,
                                nextNode: nextNode,
                                createdTime: log['_source']['created_time'],
                            })
                        }

                        // case bot say normal
                        let botSay = {
                            type: 'bot',
                            content: Array.isArray(log['_source'].output) ? log['_source'].output[0].prompt : log['_source'].output.prompt,
                            decisionNode: decisionNode,
                            audioLink: audioLink,
                            audioSlug: audioSlug,
                            audioName: audioName,
                            nextNode: nextNode,
                            createdTime: log['_source']['created_time'],
                        }
                        if (botSay.content !== '_NOT_SHOWN_') {
                            list.push(botSay)
                        }

                        // user say
                        let userSay = {
                            type: 'user',
                            user_attributes: log['_source']['user_attribute'],
                            input: log['_source']['input'],
                            createdTime: log['_source']['created_time'],
                        }

                        if (detailMessage.type === 'payload') {
                            nextNode = {
                                'next_node_id': log['_source']['nodes_flow'] ? log['_source']['nodes_flow']['to_node']['id'] : '',
                                'next_node_slug': log['_source']['nodes_flow'] ? log['_source']['nodes_flow']['to_node']['slug'] : '',
                            }
                            if (detailMessage.content === 'welcome') {
                                userSay.content = '-----new conversation-----'
                                list.push(userSay)
                                break
                            }

                            if (detailMessage.content === 'get_more_prompt') {
                                continue
                            }
                        } else if (detailMessage.type === 'end_call') {
                            // call has end_call but chat test does not
                            userSay.content = '-----end call-----'
                            list.shift()
                        } else {
                            nextNode = {
                                'next_node_id': log['_source']['nodes_flow']?.middle_node?.id || '',
                                'next_node_slug': log['_source']['nodes_flow']?.middle_node?.slug || '',
                            }
                            userSay.content = detailMessage.content
                            const nlpResponse = log['_source']['nlp_response']
                            const checkIntentNlp = nlpResponse && nlpResponse.data.intents && nlpResponse.data.intents[0]
                            userSay.nlp = {
                                intent: checkIntentNlp ? nlpResponse.data.intents[0] : null,
                                entities: nlpResponse ? nlpResponse.data.entities : []
                            }
                        }

                        list.push(userSay)

                        // decision node
                        goToNode = log['_source']['go_to_node']
                        let decisionNodeId = linkList.find(link => link.to === goToNode) ? linkList.find(link => link.to === goToNode)['from'] : ''
                        decisionNode = this.treeData.nodeList.find(node => node.id === decisionNodeId)
                    }
                    this.callSummary = logList[0]['_source']['user_attribute']['$callSummary']
                    this.activeHistoryBot.callId = logList[logList.length - 1]['_source'].call_id
                    this.activeHistoryBot.variableList = logList[logList.length - 1]['_source'].input.user_attributes
                    this.activeHistoryBot.messageList = list.reverse()
                }
            ).finally(() => {
                this.loadingMessage = false
            })
        },
        loadCallAnalysis (call) {
            // load analysis
            ScenarioService.AnalysisService.read(this.$route.params.scenarioId, call.id).then(
                result => {
                    this.analysisList = result.data.filter(item => Object.keys(item.output).length > 0)
                }
            )
        },
        newAudioPlayer () {
            this.newFeatureAudio = !this.newFeatureAudio
            localStorage.setItem('newFeatureAudio', this.newFeatureAudio)
        }
    },
    watch: {
        timeRange: 'resetSearch',
        activeCall: {
            immediate: true,
            deep: true,
            handler: function () {
                if (!!this.activeCall.id) {
                    this.loadCallMessage(this.activeCall)
                    this.loadCallAnalysis(this.activeCall)
                }
            }
        }
    },
}
</script>

<style scoped lang="scss">
@import "chat.scss";

.page-header {
    height: 60px;
    display: flex;
    align-items: center;
    background: #FFFFFF !important;
    padding: 0 20px;
    border-bottom: 1px solid #E4EDFA;

    .page-title {
        font-family: "open sans bold", serif;
        font-size: 22px;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 42px 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}


.name {
    color: #0052CC;
    cursor: pointer;
}

.ScenarioHistoryBody {
    height: calc(100vh - 160px);
    margin: 20px;
    background: #fff;
    font-size: 14px;
    border-radius: 5px;
    overflow: hidden;
    font-family: "open sans", serif;
    box-shadow: 3px 3px 6px #0000000D;
    border: 1px solid #E4EDFA;

    .ScenarioHistoryBodyLeft {
        border-right: 1px solid #E4EDFA;
        width: 450px;
    }
}

.log-container {
    height: calc(100vh - 299px);
    overflow-y: auto;

    .log-item {
        padding: 12px;
        border-bottom: 1px solid #E4EDFA;
        cursor: pointer;

        .log-item-name {
            font-family: "open sans bold", serif;
        }

        &.active {
            background: #e4edfa;
            color: #0052CC;
        }
    }
}


.filter-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    border: 1px solid #DBDBDB;
    border-radius: 5px;
    cursor: pointer;
    flex: 1;
    color: #818181;

    &:nth-child(2), &:nth-child(3) {
        flex: 2;
    }
}

.chip-activated {
    background: #0052CC;
    color: #FFF;
    border-color: #0052CC;
}

.no-data {
    flex-direction: column;
    margin-top: 64px;

    img {
        width: 40%;
    }

    .no-data-title {
        margin-top: -12px;
        font-family: "open sans semi", serif;
        font-size: 16px;
        color: #545253;
    }

    .no-data-des {
        color: #9A9A9A;
        font-size: 13px;
    }
}

::v-deep {
    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }

    .v-slider__thumb {
        width: 18px !important;
        height: 18px !important;
        left: -9px !important;
        cursor: pointer;
    }
}

.detail-tabs {
    border-radius: 3px;
    overflow: hidden;
    flex: 1;
}

// Remove the audio-player-container styles as they're now in the component

// Style for active speed in dropdown
.active-speed {
    background-color: #EBF2FF !important;

    ::v-deep {
        .v-list-item__title {
            color: #0052CC !important;
            font-weight: bold;
        }
    }
}

.name {
    color: #0052CC;
    cursor: pointer;
}


b {
    font-family: "open sans semi", serif;
}

.history-tabs {
    height: 36px;

    ::v-deep {
        .v-tabs__container {
            height: 36px;
        }

        .v-tabs__item {
            text-transform: none;
            font-size: 14px;
            height: 36px;
            min-width: 0;
            padding: 0 16px;
        }

        .v-tabs__slider-wrapper {
            display: none;
        }
    }
}

.btn-tag {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    line-height: 28px;
    padding: 0 12px;
    font-size: 11px;
    text-transform: uppercase;
    font-family: "open sans semi", serif;
    white-space: nowrap;
    background: #EEF6FF;
    color: #0052CC;
    margin-right: 8px;
    border-radius: 5px;
    cursor: pointer;
    transition: 0.3s;

    &:hover {
        background: #C4DAF6;
    }
}

.btn-menu {
    background: #EEF6FF;
    user-select: none;
}


.call-audio {
    display: flex;
    align-items: center;
    margin-top: 10px;

    audio {
        width: 100%;
        height: 30px;
    }
}

</style>
